/**
 * "Jump to lot number" — one box, both catalogues.
 *
 * WHY IT EXISTS
 * -------------
 * CEO review, 18 August 2026: walking a 173-lot Tattersalls catalogue with
 * Previous / Next is not navigation. Keeneland September, which is next in the
 * queue, catalogues around 4,500. Prev/Next stays — it is still the right
 * control for browsing — and this sits beside it for "Saeed said look at 147".
 *
 * WHY IT IS SHARED
 * ----------------
 * The two surfaces it serves are structurally different. The International
 * Auctions lot route is ALREADY keyed by lot number
 * (`/international-auctions/:house/:sale/:lot`), so a jump there is one
 * `routerLink`. The native buyer route is keyed by horse SLUG
 * (`/buyer/auctions/:auction/horse/:slug`), so a jump there has to resolve a
 * number to a slug first. What they share is everything the visitor touches:
 * the matching rules, the digit folding, the suggestion list, the keyboard, and
 * the four things that can go wrong. That is what lives here. Each page keeps
 * its own idea of what a resolved entry means and does its own navigating.
 *
 * WHAT IT DOES NOT DO
 * -------------------
 * It never fetches. The index of lot numbers is the parent's business — it
 * knows the endpoint, the cache and the sale — and this asks for it exactly
 * once, by emitting `load` on first focus. Until the entries arrive, a submit
 * is remembered and replayed rather than answered wrongly.
 */
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
  OnChanges,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

/** One jumpable lot, as the parent knows it. */
export interface LotJumpEntry {
  /**
   * The number the visitor types AND the token the parent navigates with —
   * `"147"`, or `"26A"` where the house uses suffixes. A string in both
   * catalogues even though the native one stores an integer: the box compares
   * text, and the parent casts back if it needs to.
   */
  value: string;
  /** Horse name / display title. Shown beside the number in the suggestions. */
  label: string;
  /**
   * A lot that is IN the catalogue but is not a legitimate destination —
   * withdrawn, on the native side. Answered with `blockedMessageKey` instead of
   * a navigation, so a visitor reading a printed catalogue is told what
   * happened rather than dropped on a dead page.
   */
  blocked?: boolean;

  /**
   * Translation key for a badge beside the name in the suggestion list —
   * `lot_jump.tag_withdrawn`, typically.
   *
   * SEPARATE from `blocked` on purpose. The International Auctions lot profile
   * renders a withdrawn lot perfectly well, with its own "withdrawn from the
   * sale" notice, so there the state is worth SHOWING and not worth blocking.
   * On the native side the same state blocks. One flag could not say both.
   */
  tagKey?: string;
}

/** The state the input is in, for the message under it. */
type JumpError = 'not_found' | 'blocked' | null;

@Component({
  selector: 'app-lot-jump',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './lot-jump.component.html',
  styleUrl: './lot-jump.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotJumpComponent implements OnChanges {
  /**
   * The HOUSE's word for a lot number — `Hip` at Fasig-Tipton, `Lot` at
   * Tattersalls, whatever `house.lot_label` says. Never a translation key: the
   * label is the partner's vocabulary, not ours.
   */
  @Input() lotLabel = 'Lot';

  /**
   * `null` means "not asked yet" and is NOT the same as `[]`, which means "this
   * catalogue genuinely has no numbered lots". The first hides nothing; the
   * second hides the box.
   */
  @Input() entries: LotJumpEntry[] | null = null;

  /** True while the parent is fetching the index. */
  @Input() loading = false;

  /**
   * Which message a blocked entry gets. The two catalogues block for different
   * reasons and only the parent knows which — `withdrawn` on the native side,
   * nothing on the international one, where a withdrawn lot has a real page
   * that says so.
   */
  @Input() blockedMessageKey = 'lot_jump.withdrawn';

  /** `ia` on the International Auctions pages, `buyer` on the native ones. */
  @Input() variant: 'ia' | 'buyer' = 'ia';

  /**
   * Emitted once, on first focus. The parent answers by filling `entries`.
   * Deliberately lazy: on the international lot page this is a second request
   * per sale, and a visitor who never uses the box never pays for it.
   */
  @Output() load = new EventEmitter<void>();

  /** A resolved, non-blocked destination. The parent navigates. */
  @Output() jump = new EventEmitter<LotJumpEntry>();

  @ViewChild('field') private field?: ElementRef<HTMLInputElement>;

  /**
   * Unique per instance, so `aria-activedescendant` points at THIS box's
   * options. Counted rather than randomised: SSR renders the markup and the
   * browser hydrates it, and a random id would differ between the two.
   */
  private static seq = 0;
  readonly uid = `lot-jump-${(LotJumpComponent.seq += 1)}`;

  text = '';
  error: JumpError = null;
  /** The number the message is about — held so it survives the input clearing. */
  errorValue = '';
  open = false;
  /** Index into `suggestions`, or -1 for "nothing highlighted". */
  activeIndex = -1;

  /** True once `load` has gone out, so focus does not re-ask on every tab. */
  private asked = false;
  /**
   * Set when the visitor submits before the index has arrived. Replayed in
   * `ngOnChanges` — a fast typist must not be told "no such lot" because the
   * request was still in flight.
   */
  private pendingSubmit = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entries'] && this.entries !== null && this.pendingSubmit) {
      const typed = this.pendingSubmit;
      this.pendingSubmit = '';
      this.commit(typed);
    }
  }

  /* ---------------------------------------------------------------- *
   * Derived
   * ---------------------------------------------------------------- */

  /** Hidden entirely when the catalogue has nothing to jump to. */
  get isUsable(): boolean {
    return this.entries === null || this.entries.length > 0;
  }

  /** `1–173`, for the hint beside the field. Empty until the index arrives. */
  get range(): string {
    const list = this.entries;
    if (!list || list.length === 0) {
      return '';
    }
    const first = list[0].value;
    const last = list[list.length - 1].value;
    return first === last ? first : `${first}–${last}`;
  }

  /**
   * Up to eight entries whose number STARTS WITH what has been typed. Prefix
   * rather than substring: typing `2` in a 173-lot sale should offer 2, 20-29
   * and 200-odd — not every number containing a 2.
   */
  get suggestions(): LotJumpEntry[] {
    const key = this.normalise(this.text);
    if (!key || !this.entries) {
      return [];
    }
    return this.entries
      .filter((entry) => this.normalise(entry.value).startsWith(key))
      .slice(0, 8);
  }

  /* ---------------------------------------------------------------- *
   * Interaction
   * ---------------------------------------------------------------- */

  onFocus(): void {
    this.open = true;
    if (!this.asked && this.entries === null) {
      this.asked = true;
      this.load.emit();
    }
  }

  /**
   * A click outside closes the list. `relatedTarget` keeps a click ON a
   * suggestion from closing it before the click lands.
   */
  onBlur(event: FocusEvent): void {
    const next = event.relatedTarget as HTMLElement | null;
    if (next?.closest('.lot-jump__list')) {
      return;
    }
    this.open = false;
    this.activeIndex = -1;
  }

  onInput(event: Event): void {
    this.text = (event.target as HTMLInputElement).value;
    this.error = null;
    this.open = true;
    this.activeIndex = -1;
  }

  onKeydown(event: KeyboardEvent): void {
    const list = this.suggestions;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.open = true;
        this.activeIndex = list.length ? (this.activeIndex + 1) % list.length : -1;
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.open = true;
        this.activeIndex = list.length
          ? (this.activeIndex - 1 + list.length) % list.length
          : -1;
        break;

      case 'Escape':
        this.open = false;
        this.activeIndex = -1;
        break;

      case 'Enter':
        event.preventDefault();
        if (this.activeIndex >= 0 && list[this.activeIndex]) {
          this.choose(list[this.activeIndex]);
        } else {
          this.submit();
        }
        break;

      default:
        break;
    }
  }

  /** The button, and Enter with nothing highlighted. */
  submit(): void {
    const typed = this.text;
    if (!this.normalise(typed)) {
      this.error = null;
      return;
    }

    // The index is still in flight. Remember the intent rather than answering
    // "no such lot" — `ngOnChanges` replays this the moment it lands.
    if (this.entries === null) {
      this.pendingSubmit = typed;
      if (!this.asked) {
        this.asked = true;
        this.load.emit();
      }
      return;
    }

    this.commit(typed);
  }

  /** A suggestion clicked or chosen with Enter. */
  choose(entry: LotJumpEntry): void {
    this.text = entry.value;
    this.commit(entry.value);
  }

  /* ---------------------------------------------------------------- *
   * Resolution
   * ---------------------------------------------------------------- */

  private commit(typed: string): void {
    const entry = this.resolve(typed);
    this.open = false;
    this.activeIndex = -1;
    this.errorValue = this.normalise(typed);

    if (!entry) {
      this.error = 'not_found';
      return;
    }

    if (entry.blocked) {
      this.error = 'blocked';
      this.errorValue = entry.value;
      return;
    }

    this.error = null;
    this.text = '';
    this.jump.emit(entry);
  }

  /**
   * Exact match first; then the number alone, so a visitor who types `26` from
   * a printed catalogue that prints `26A` still gets there. Nothing looser —
   * guessing at a lot number is worse than saying so.
   */
  private resolve(typed: string): LotJumpEntry | null {
    const key = this.normalise(typed);
    if (!key || !this.entries) {
      return null;
    }

    const exact = this.entries.find(
      (entry) => this.normalise(entry.value) === key
    );
    if (exact) {
      return exact;
    }

    return (
      this.entries.find(
        (entry) => this.normalise(entry.value).replace(/[^0-9]/g, '') === key
      ) ?? null
    );
  }

  /**
   * Arabic-Indic (`١٤٧`) and Extended Arabic-Indic (`۱۴۷`) digits fold to
   * ASCII. This is not a nicety: the Arabic site is the one this box was asked
   * for, and an Arabic keyboard produces U+0660–0669. Without this it fails
   * silently for exactly the audience it is meant to serve.
   *
   * Spaces go, and the rest uppercases so `26a` finds `26A`.
   */
  private normalise(raw: string): string {
    let out = '';

    for (const character of raw) {
      const code = character.codePointAt(0) ?? 0;

      if (code >= 0x0660 && code <= 0x0669) {
        out += String(code - 0x0660);
      } else if (code >= 0x06f0 && code <= 0x06f9) {
        out += String(code - 0x06f0);
      } else {
        out += character;
      }
    }

    return out.replace(/\s+/g, '').toUpperCase();
  }
}
